import APIService from '../../../services/APIService';
import {
  AnyModule,
  CreateModulePayload,
  ModuleListParams,
  PaginatedModulesResponse,
  PublicModule,
  UpdateModulePayload,
  UserModuleAdmin,
} from './module.types';

// Import your existing API utility if available]

const BASE_URL = '/admin/modules';

/**
 * Fetches modules with optional pagination, filtering, and sorting
 */
export const fetchModules = async (
  params: ModuleListParams = {},
  accessToken: string,
): Promise<PaginatedModulesResponse> => {
  // Convert params object to URL query parameters
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, String(value));
    }
  });

  const response = await APIService.makeAPIGetRequest({
    requestString: `${BASE_URL}?${queryParams.toString()}`,
    accessToken,
  });

  if (response.ok) {
    return response.value as PaginatedModulesResponse;
  }

  throw new Error('Failed to fetch modules');
};

/**
 * Fetches a single module by ID and version
 */
export const fetchModule = async (
  moduleId: string,
  moduleVersion: number,
  moduleType: 'public' | 'user',
  accessToken: string,
): Promise<AnyModule> => {
  const response = await APIService.makeAPIGetRequest({
    requestString: `${BASE_URL}/${moduleId}/${moduleVersion}?type=${moduleType}`,
    accessToken,
  });

  if (response.ok) {
    return response.value as AnyModule;
  }

  throw new Error(`Failed to fetch module ${moduleId}/${moduleVersion}`);
};

/**
 * Creates a new module
 */
export const createModule = async (
  moduleData: CreateModulePayload,
  accessToken: string,
): Promise<AnyModule> => {
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/create`,
    accessToken,
    body: moduleData,
  });

  if (response.ok) {
    return response.value as AnyModule;
  }

  throw new Error('Failed to create module');
};

/**
 * Updates an existing module
 */
export const updateModule = async (
  moduleId: string,
  moduleVersion: number,
  moduleType: 'public' | 'user',
  moduleData: UpdateModulePayload,
  accessToken: string,
): Promise<AnyModule> => {
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/update?module_id=${moduleId}&module_version=${moduleVersion}&type=${moduleType}`,
    accessToken,
    body: moduleData,
  });

  if (response.ok) {
    return response.value as AnyModule;
  }

  throw new Error(`Failed to update module ${moduleId}/${moduleVersion}`);
};

/**
 * Deletes a public module
 */
export const deletePublicModule = async (
  moduleId: string,
  moduleVersion: number,
  accessToken: string,
): Promise<void> => {
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/delete?module_id=${moduleId}&module_version=${moduleVersion}&type=public`,
    accessToken,
  });

  if (!response.ok) {
    throw new Error(`Failed to delete module ${moduleId}/${moduleVersion}`);
  }
};

/**
 * Deactivates a user module (soft delete)
 */
export const deactivateUserModule = async (
  moduleId: string,
  moduleVersion: number,
  accessToken: string,
): Promise<void> => {
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/${moduleId}/${moduleVersion}/user/deactivate`,
    accessToken,
    body: { deactivated: true },
  });

  if (!response.ok) {
    throw new Error(`Failed to deactivate module ${moduleId}/${moduleVersion}`);
  }
};

/**
 * Activates a previously deactivated user module
 */
export const activateUserModule = async (
  moduleId: string,
  moduleVersion: number,
  accessToken: string,
): Promise<void> => {
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/${moduleId}/${moduleVersion}/user/activate`,
    accessToken,
    body: { deactivated: false },
  });

  if (!response.ok) {
    throw new Error(`Failed to activate module ${moduleId}/${moduleVersion}`);
  }
};
